import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, opinionSessionsApi } from '../services/api'

interface GenerateOpinionRequest {
  query: string
  instructions?: string
  documentContent?: string
  useAnonymization?: boolean
  role?: string
  docType?: string
  useUodo?: boolean
  useUzp?: boolean
  useSn?: boolean
  useEurlex?: boolean
  useCaseLaw?: boolean
  projectId?: string
}

export interface ActiveGeneration {
  status: 'generating' | 'completed' | 'error'
  sessionId: string
  projectId: string
  query: string
  error?: string
}

interface GenerationContextType {
  activeGeneration: ActiveGeneration | null
  startGeneration: (sessionId: string, params: GenerateOpinionRequest) => void
  clearGeneration: () => void
}

const GenerationContext = createContext<GenerationContextType | undefined>(undefined)

export function GenerationProvider({ children }: { children: React.ReactNode }) {
  const [activeGeneration, setActiveGeneration] = useState<ActiveGeneration | null>(null)
  const queryClient = useQueryClient()
  const abortRef = useRef(false)

  const startGeneration = useCallback((sessionId: string, params: GenerateOpinionRequest) => {
    abortRef.current = false

    setActiveGeneration({
      status: 'generating',
      sessionId,
      projectId: params.projectId || '',
      query: params.query,
    })

    api.post('/generation/opinion', params)
      .then(async (res) => {
        if (abortRef.current) return

        const data = res.data

        // Extract text and metadata (same logic as OpinionGeneratorPage)
        let fullText = ''
        let metadata: any
        let externalSessionId: string | undefined

        if (Array.isArray(data)) {
          fullText = data.map((chunk: { text?: string }) => chunk.text || '').join('')
          const lastChunk = data.find((chunk: any) => {
            const isFinal = chunk.isFinal || chunk.IsFinal || chunk.is_final
            const hasMetadata = chunk.metadata || chunk.Metadata
            return isFinal && hasMetadata
          })
          if (lastChunk) {
            metadata = lastChunk.metadata || lastChunk.Metadata
            externalSessionId = metadata?.sessionId || metadata?.session_id || metadata?.SessionId
          }
        } else if (data.chunks) {
          fullText = data.full_text || data.chunks.map((c: { text?: string }) => c.text || '').join('')
          metadata = data.metadata
          externalSessionId = data.metadata?.sessionId || data.session_id
        } else {
          fullText = data.content || data.text || data.opinion || ''
          metadata = data.metadata
          externalSessionId = data.metadata?.sessionId || data.session_id
        }

        // Save result as a turn on the session
        const md = metadata || {}
        const detailedSources = md.detailedSources || md.detailed_sources || md.DetailedSources || []
        const citVerified = md.citationsVerified ?? md.citations_verified ?? 0
        const citTotal = md.citationsTotal ?? md.citations_total ?? 0
        const sourcesJson = detailedSources.length > 0 ? JSON.stringify(detailedSources) : undefined

        await opinionSessionsApi.addTurn(sessionId, {
          opinionText: fullText,
          type: 'Generation',
          externalSessionId,
          sourcesUsed: sourcesJson,
          citationsVerified: citVerified,
          citationsTotal: citTotal,
          metadata: metadata ? JSON.stringify({ generationTimeMs: md.generationTimeMs || md.generation_time_ms }) : undefined,
        })

        // Invalidate session queries so sidebar/popup see the update
        queryClient.invalidateQueries({ queryKey: ['opinion-sessions'] })

        if (!abortRef.current) {
          setActiveGeneration(prev => prev ? { ...prev, status: 'completed' } : null)
        }
      })
      .catch((err) => {
        if (abortRef.current) return
        console.error('[GenerationContext] Generation failed:', err)
        setActiveGeneration(prev => prev ? {
          ...prev,
          status: 'error',
          error: err?.response?.data?.error || err?.message || 'Generation failed',
        } : null)
      })
  }, [queryClient])

  const clearGeneration = useCallback(() => {
    abortRef.current = true
    setActiveGeneration(null)
  }, [])

  return (
    <GenerationContext.Provider value={{ activeGeneration, startGeneration, clearGeneration }}>
      {children}
    </GenerationContext.Provider>
  )
}

export function useGeneration() {
  const context = useContext(GenerationContext)
  if (context === undefined) {
    throw new Error('useGeneration must be used within a GenerationProvider')
  }
  return context
}
